import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import { Box, Divider, type SxProps, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { type FC, type MouseEvent, memo, useMemo, useState } from "react";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useLocationCredentialsQueryHook } from "../../../pages/Profile/v2/SynergyCredentials/credentialsHooks";
import { INK, NEUTRAL } from "../../../theme/tokens";
import type { OpenShiftRequest, ReplaceShiftRequest, ShiftRequest } from "../../../utils/models";
import { facilityToString } from "../../../utils/utilFunctions";
import { NormalizedRequestApprovalDialog } from "../../approvalDialogs/RequestsApproval/NormalizedRequestApprovalDialog";
import { MessageWorkingFilterEnum } from "../../StaffCommunication/util/Broadcast/MessageFilter";
import { NewBroadcastMessageModal } from "../../StaffCommunication/util/Broadcast/NewBroadcastMessageModal";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";

type Props = {
	request: ShiftRequest;
	onDelete: (event: MouseEvent<HTMLElement>, request: ShiftRequest) => void;
};

const smallButtonSx = { fontSize: "12px", height: "28px" } as const satisfies SxProps;

export const MedicalDepartmentRequestItem: FC<Props> = memo(({ request, onDelete }) => {
	const theme = useTheme();
	const { chosenDepartment, hospitalData } = useHospitalData();
	const smsPickupEnabled = useFeatureFlag(FEATURE_FLAGS.SmsPickup);
	const [showBroadcastModal, setShowBroadcastModal] = useState(false);
	const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);

	// Find the matching DepartmentLocation for this request's location
	const matchingLocation = useMemo(
		() => chosenDepartment.locations.find((loc) => loc.location === request.location),
		[chosenDepartment.locations, request.location],
	);

	const { credentialedNurseIds } = useLocationCredentialsQueryHook({
		locationId: matchingLocation?.id ?? null,
		hideExpired: true,
		onlyShowApproved: true,
	});

	const approvedNurses = useMemo(
		() => request.message.request_data.filter(({ nurse_id }) => !!nurse_id).map(({ nurse_id }) => nurse_id),
		[request.message.request_data],
	);

	// Nurses to ignore in broadcast = all non-credentialed + those who already responded
	const nurseIdsToIgnoreForBroadcast = useMemo(() => {
		const credentialedSet = new Set(credentialedNurseIds);
		const nonCredentialed = hospitalData.nurses.filter((n) => !credentialedSet.has(n.id)).map((n) => n.id);
		return [...new Set([...nonCredentialed, ...approvedNurses])];
	}, [credentialedNurseIds, hospitalData.nurses, approvedNurses]);

	return (
		<>
			<Box
				data-testid={`request-card-${request.id}`}
				component={motion.div}
				whileHover={{
					scale: 1.02,
					boxShadow: `0 4px 2px ${theme.palette.primary.light}`,
					transition: { duration: 0.1, ease: "easeInOut" },
				}}
				sx={{
					mx: 0.45,
					borderRadius: "8px",
					my: 2,
					p: 1.5,
					color: INK.SECONDARY,
					border: `1px solid ${NEUTRAL[200]}`,
					transition: "border 0.5s ease",
					"&:hover": {
						border: `2px solid ${theme.palette.primary.main}`,
					},
				}}
			>
				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					{/* Date display only */}
					<Box>
						<Typography sx={{ fontWeight: 600, fontSize: "14px" }}>{request.message.shift_start.toFormat("ccc, MMM d")}</Typography>
						{matchingLocation && <Typography sx={{ fontSize: "12px" }}>{facilityToString(matchingLocation)}</Typography>}
					</Box>
					{/* Action buttons */}
					<Box sx={{ display: "flex" }}>
						<SystemButton
							data-testid={`medical-request-broadcast-${request.id}`}
							leadingIcon={<ChatOutlinedIcon fontSize="small" />}
							tooltip="Message credentialed staff"
							iconButton
							onClick={(e) => {
								e.stopPropagation();
								setShowBroadcastModal(true);
							}}
						/>
						<SystemButton
							data-testid={`request-delete-${request.id}`}
							leadingIcon={<DeleteOutlined fontSize="small" />}
							tooltip="Delete Request"
							iconButton
							sx={{ "&:hover": { backgroundColor: "hsla(7, 82%, 39%, 0.10)" } }}
							onClick={(event) => onDelete(event, request)}
						/>
					</Box>
				</Box>

				{approvedNurses.length > 0 && (
					<>
						<Divider sx={{ my: 1 }} />
						<SystemButton data-testid={`medical-request-show-all-${request.id}`} sx={smallButtonSx} onClick={() => setIsApprovalDialogOpen(true)}>
							Show All ({approvedNurses.length})
						</SystemButton>
					</>
				)}
			</Box>

			{showBroadcastModal && (
				<NewBroadcastMessageModal
					initialSmsMessage={
						smsPickupEnabled
							? `Reply YES to pickup ${request.message.shift_start.toFormat("ccc, MMM d")}, ${request.message.shift_start.toFormat("h:mma")}\u2013${request.message.shift_end.toFormat("h:mma")} shift.`
							: `Hi,\nWould you like to pick up the ${request.message.shift_start.toFormat("ccc, MMM d")} shift?\n\nSubmit your request in the In-House app.`
					}
					defaultWorkingFilter={MessageWorkingFilterEnum.STAFF_THAT_DOES_NOT_WORK}
					nurseIdsToIgnore={nurseIdsToIgnoreForBroadcast}
					jobId={request.job_id}
					onClose={() => setShowBroadcastModal(false)}
					initialFilters={[
						{
							workFilter: MessageWorkingFilterEnum.STAFF_THAT_DOES_NOT_WORK,
							dateFilter: request.message.date,
							timeFilter: {
								start: request.message.shift_start.startOf("day"),
								end: request.message.shift_end.endOf("day"),
							},
						},
					]}
				/>
			)}

			{isApprovalDialogOpen && (
				<NormalizedRequestApprovalDialog
					open={true}
					request={request as OpenShiftRequest | ReplaceShiftRequest}
					onClose={() => setIsApprovalDialogOpen(false)}
					shouldNotAllowToMaximize={true}
				/>
			)}
		</>
	);
});
